/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, CircularProgress, Box, TextField, Stack, styled } from '@mui/material';

import { ISearchableDropDownProps } from '../Graphs/Graphs.Model';
import { itemsPerPage } from '../../../Utilities/utils';

import { Icons } from '../../../Constants/Icons';
import theme from '../../../Themes/theme';

import { FilterField } from '../GraphContainer/GraphContainer.styles';
import { JobsTitle } from '../../../../Modules/Jobs/Components/IssuesRaised/JobsIssuesRaised.styles';
import { StyledPagination } from '../CommonStyle/Pagination.Style';
import { buttonStyles } from '../../../../Modules/Facility/Components/AddFacility/AddFacility.Style';
import { StyleConstants } from '../../../Constants/Style.Constants';

const SearchableDropDown: React.FC<ISearchableDropDownProps> = ({
  options,
  selectedValue,
  onChange,
  isLoading,
  title,
  totalCount,
  handlePaginationChange,
  currentPage,
  searchString = '',
  setSearchString,
  dropDownWidth,
  iconLoader,
}) => {
  const { searchTextField } = buttonStyles;
  const { t } = useTranslation(['assignJob', 'oneTimeJob']);

  const CustomListbox = styled('ul')({
    borderRadius: '16px',
    padding: 0,
    margin: 0,
    '& li': {},
  });

  const handleClearSearch = () => {
    setSearchString('');
    onChange({ target: { value: '', label: '' } });
  };

  const filteredOptions = options?.filter((option) =>
    option?.label?.toLowerCase()?.includes(searchString?.toLowerCase() || '')
  );

  return (
    <FilterField>
      <JobsTitle sx={{ fontSize: theme.typography.h4?.fontSize }}>{title}</JobsTitle>
      <Box sx={{ width: dropDownWidth ? dropDownWidth : 'auto', maxWidth: '100%' }}>
        <Autocomplete
          options={filteredOptions}
          getOptionLabel={(option) => option.label || ''}
          value={options.find((opt) => opt.value === selectedValue) || null}
          onChange={(event, newValue) => {
            if (newValue) {
              onChange({ target: { value: newValue.value, label: newValue.label } });
              setSearchString(newValue.label);
            } else {
              onChange({ target: { value: null, label: null } });
              setSearchString('');
            }
          }}
          loading={isLoading}
          componentsProps={{
            paper: {
              sx: {
                borderRadius: '1rem',
                overflow: 'hidden',
              },
            },
          }}
          loadingText={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px',
              }}>
              <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
            </Box>
          }
          inputValue={searchString}
          onInputChange={(event, newInputValue) => {
            setSearchString(newInputValue);
          }}
          ListboxProps={{
            sx: {
              maxHeight: 600,
              padding: 0,
              margin: 0,
              borderRadius: '16px',
              overflowY: 'auto',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            },
          }}
          ListboxComponent={(props) => (
            <CustomListbox {...props}>
              {isLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1,
                  }}>
                  <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                </Box>
              )}
              {props.children}
              {totalCount > 10 && (
                <Stack spacing={2} justifyContent="center" sx={{ marginTop: '1rem', padding: '0.5rem' }}>
                  <StyledPagination
                    count={Math.ceil(totalCount / itemsPerPage)}
                    page={currentPage}
                    onChange={handlePaginationChange}
                    color="primary"
                  />
                </Stack>
              )}
            </CustomListbox>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('oneTimeJob:searchJobs')}
              value={searchString}
              onChange={(e) => {
                setSearchString(e.target.value);
              }}
              sx={{ ...searchTextField }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {iconLoader ? (
                      <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                    ) : searchString ? (
                      <img
                        src={Icons.CancelIcon}
                        alt="clear"
                        onClick={handleClearSearch}
                        style={{
                          cursor: 'pointer',
                          height: '1.25rem',
                          width: '1.25rem',
                          filter:
                            'invert(26%) sepia(57%) saturate(546%) hue-rotate(130deg) brightness(90%) contrast(92%)',
                        }}
                      />
                    ) : (
                      <img
                        src={Icons.SearchIcon}
                        alt="search"
                        style={{
                          height: '1.25rem',
                          width: '1.25rem',
                          filter:
                            'invert(26%) sepia(57%) saturate(546%) hue-rotate(130deg) brightness(90%) contrast(92%)',
                        }}
                      />
                    )}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option, { selected }) => {
            const isSelected = option.value === selectedValue;

            return (
              <li
                {...props}
                style={{
                  padding: '12px',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.h3?.fontSize,
                  fontWeight: isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightLight,
                  lineHeight: '1.375rem',
                  letterSpacing: StyleConstants.LetterSpacingTight,
                  backgroundColor: isSelected ? theme.palette.primary.main : 'transparent',
                  borderBottom: `0.0625rem solid ${theme.palette.divider}`,
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.palette.secondary.dark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selected ? theme.palette.primary.main : 'transparent';
                }}>
                {option.label}
              </li>
            );
          }}
          popupIcon={null}
          clearIcon={null}
        />
      </Box>
    </FilterField>
  );
};

export default SearchableDropDown;
